/**
 * One invoice: its figures and dates, its lifecycle, its lines, its email, and its version history.
 *
 * The port of `Views/InvoiceDetailPage.xaml` + `InvoiceDetailViewModel`. What carries over, and why:
 *
 *   * DRAFT AND ISSUED ARE EDITABLE; PAID AND VOID ARE FROZEN. The due-date editor, the line editor
 *     and every Restore button exist only while the server would accept them.
 *   * "HAS THIS BEEN SENT, AND DID IT ARRIVE?" SITS IN THE SUMMARY, not behind the Email button - it
 *     is asked far more often than "send it again". A failed most-recent attempt says so rather than
 *     falling back to an older success, and a bounce is red there rather than a row further down.
 *   * THE COMPOSE PANEL KEEPS WHAT WAS TYPED ON A REFUSAL. Closing it would make the sender retype the
 *     message to correct one address. A picked recipient is APPENDED - "...and their bookkeeper" is
 *     what the picker is for - and picking the same one twice does nothing.
 *   * A blocked send is a SENTENCE saying why, never a silently disabled button.
 *
 * One deliberate difference: the lifecycle buttons are offered only where the status allows them
 * (Issue on a draft, Mark paid on an issued invoice, Void on either). The XAML offered all three
 * always and let the server refuse, which is a button whose only job is to produce an error.
 *
 * Dates: due, issued and paid are CALENDAR dates - stored at UTC midnight and printed, never
 * converted. A line's date is a day in the reader's life, so it is pinned to LOCAL noon on the way out
 * and shown in local time on the way back, the same round trip the C# view model made.
 */
import { TextArea, type Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import {
  InvoicePaymentStatus,
  InvoiceStatus,
  type InboundMailDto,
  type InvoicePaymentDto,
  type InvoicePaymentsDto,
  type InvoiceDto,
  type InvoiceEmailDto,
  type InvoiceLineDto,
  type InvoiceRecipientDto,
  type InvoiceVersionDto
} from '../contracts.gen.js';
import { openInNewTab } from '../download.js';
import { calendarUtc, isEditable, lastSentLine, localIsoOf, localNoonUtc, todayLocalIso } from '../invoicing.js';
import { join, localDate, localDateTime, money, parseAmount, plainDate } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { dateBox } from '../ui/DateBox.js';
import { confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { StatusLine } from '../ui/StatusLine.js';
import { button, buttonRow, card, checkBox, field, fieldRow, fillWith, FONT, GAP, h1, h2, hstack, inlineRow, muted, relayout, scrollPage, splitRow, stackOf, text, textBox, value, vstack } from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { invoiceChip } from './sections/invoice-card.js';

export function invoiceDetailPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const invoiceId = Number(ctx.params.id);
      let invoice: InvoiceDto | null = null;
      let editingLineId: number | null = null;
      let busy = false;
      let sending = false;

      const status = new StatusLine();

      // =================================================================
      //  Header and summary
      // =================================================================

      const back = link('← Invoices', () => ctx.go('/invoices'));
      const title = h1('Loading…');
      const chipHost = hstack({ spacing: GAP.sm });
      const customerLink = link('', () => {
        if (invoice && !invoice.customerDeleted) ctx.go(`/customers/${invoice.customerId}`);
      });
      // Chips on the line under the title, not at the end of the title's row: a chip there was
      // given the h1 row's height and drawn clipped.
      const subRow = inlineRow({ spacing: GAP.md });
      subRow.controls.add(chipHost);
      subRow.controls.add(customerLink);

      const datesLine = text('');
      const moneyLine = value('', { font: FONT.bold });

      const due = dateBox();
      const saveDueButton = button('Save due date', () => void saveDueDate());
      const dueEditor = stackOf(
        buttonRow({ align: 'end' }),
        field('Due date', due.picker, { width: 170 }),
        saveDueButton
      );

      const pdfButton = button('PDF', () => openPdf());
      const emailButton = button('Email', () => void openEmail());
      const issueButton = button('Issue', () => void lifecycle('issue'), true);
      const paidButton = button('Mark paid', () => void lifecycle('paid'), true);
      const voidButton = button('Void', () => void lifecycle('void'));
      const actions = stackOf(buttonRow(), pdfButton, emailButton, issueButton, paidButton, voidButton);

      const sentLine = muted('');

      const summary = card({ visible: false });
      summary.controls.add(
        stackOf(vstack({ spacing: GAP.md }), datesLine, moneyLine, dueEditor, actions, sentLine)
      );

      // =================================================================
      //  Email compose
      // =================================================================

      const blocked = text('');
      const blockedCard = card({ tint: 'warn', visible: false });
      blockedCard.controls.add(blocked);

      const toBox = textBox();
      const recipients = buttonRow();
      const ccBox = textBox();
      const subjectBox = textBox();
      const bodyBox = new TextArea({ height: 170, hAlign: 'stretch', wrap: true });
      const attachPdf = checkBox('Attach the invoice PDF', true);
      const markIssued = checkBox('Mark the invoice as issued when it is sent', false);
      const sendButton = button('Send', () => void send(), true);
      const emailStatus = new StatusLine();

      const compose = card({ visible: false });
      compose.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Email this invoice'),
          blockedCard,
          field('To', toBox),
          recipients,
          field('Cc (optional)', ccBox),
          field('Subject', subjectBox),
          field('Message', bodyBox),
          attachPdf,
          markIssued,
          stackOf(buttonRow(), sendButton, button('Cancel', () => closeEmail())),
          emailStatus.label
        )
      );

      // =================================================================
      //  Pay online
      // =================================================================

      const payState = muted('');
      // A read-only box rather than a label: canvas text cannot be selected, and a technician who
      // wants the link in a chat window has to be able to select it when the clipboard is refused.
      const payUrlBox = textBox({ readOnly: true });
      const copyPayButton = button('Copy link', () => void copyPayLink());
      const openPayButton = button('Open', () => {
        if (payUrlBox.text) window.open(payUrlBox.text, '_blank', 'noopener');
      });
      const payLinkRow = stackOf(vstack({ spacing: GAP.sm }), field('Customer pay link', payUrlBox),
        stackOf(buttonRow(), copyPayButton, openPayButton));
      const payWarning = muted('', 'warn');
      const payStatus = new StatusLine();
      const paymentList = new CardList<InvoicePaymentDto>({
        key: p => p.id,
        version: p => [p.status, p.captureId, p.error],
        build: p => buildPaymentRow(p),
        spacing: GAP.sm
      });
      const paySection = card({ visible: false });
      paySection.controls.add(
        stackOf(vstack({ spacing: GAP.md }), h2('Pay online'), payState, payLinkRow, payWarning, paymentList.panel, payStatus.label)
      );

      // =================================================================
      //  Send log and correspondence
      // =================================================================

      const sentList = new CardList<InvoiceEmailDto>({
        key: e => e.id,
        build: e => buildSentRow(e),
        spacing: GAP.sm
      });
      const sentSection = stackOf(vstack({ spacing: GAP.sm, visible: false }), h2('Email history'), sentList.panel);

      const mailList = new CardList<InboundMailDto>({
        key: m => m.id,
        build: m => buildMailRow(m),
        spacing: GAP.sm
      });
      const mailSection = stackOf(vstack({ spacing: GAP.sm, visible: false }), h2('Correspondence'), mailList.panel);

      // =================================================================
      //  Lines
      // =================================================================

      const linesHost = vstack({ spacing: GAP.sm });
      const linesEmpty = new EmptyState('This invoice has no lines.');

      const editorTitle = h2('Add a line');
      const lineDate = dateBox({ iso: todayLocalIso() });
      const lineDescription = textBox();
      const lineQuantity = textBox({ text: '1' });
      const lineRate = moneyBox();
      const lineNotes = textBox();
      const saveLineButton = button('Add line', () => void saveLine(), true);
      const cancelLineButton = button('Cancel', () => resetLineEditor());
      // The editor and the history each say their own outcome: the page's status line is at the top,
      // scrolled out of view by the time someone is down here, and a refusal nobody can see reads as
      // a button that did nothing.
      const lineStatus = new StatusLine();
      const historyStatus = new StatusLine();
      cancelLineButton.visible = false;

      const lineEditor = card({ visible: false });
      lineEditor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('Date', lineDate.picker), field('Description', lineDescription)),
          fieldRow(field('Quantity', lineQuantity), field('Rate', lineRate.row)),
          field('Notes', lineNotes),
          stackOf(buttonRow(), saveLineButton, cancelLineButton),
          lineStatus.label
        )
      );

      const linesSection = stackOf(
        vstack({ spacing: GAP.sm, visible: false }),
        h2('Line items'),
        linesEmpty.panel,
        linesHost,
        lineEditor
      );

      // =================================================================
      //  History
      // =================================================================

      const historyList = new CardList<InvoiceVersionDto>({
        key: v => v.version,
        // Restore's presence depends on the invoice's status, not on the version.
        version: v => [v.version, isEditable(invoice)],
        build: v => buildVersionCard(v),
        spacing: GAP.sm
      });
      const historySection = stackOf(
        vstack({ spacing: GAP.sm, visible: false }),
        h2('History'),
        historyStatus.label,
        historyList.panel
      );

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          stackOf(vstack({ spacing: GAP.xs }), back, title, subRow),
          status.label,
          summary,
          paySection,
          compose,
          sentSection,
          mailSection,
          linesSection,
          historySection
        )
      );
      host.controls.add(view);

      // =================================================================
      //  Rendering
      // =================================================================

      function applyInvoice(i: InvoiceDto): void {
        invoice = i;
        const editable = isEditable(i);

        title.text = i.number;
        chipHost.controls.clear();
        chipHost.controls.add(invoiceChip(i));
        if (i.isRecurring) chipHost.controls.add(chip('recurring', 'info'));
        // From the chip row upward: a row filled after the first layout keeps its old size otherwise.
        relayout(chipHost);

        customerLink.text = i.customerDeleted ? `${i.customerName} (customer deleted)` : `${i.customerName} →`;

        datesLine.text = join([
          `Created ${localDate(i.createdUtc)}`,
          i.issuedUtc ? `issued ${plainDate(i.issuedUtc)}` : 'not issued',
          i.dueUtc ? `due ${plainDate(i.dueUtc)}` : 'no due date',
          i.paidUtc ? `paid ${plainDate(i.paidUtc)}` : null
        ]);
        moneyLine.text = join([
          `Subtotal ${money(i.subtotal)}`,
          `Tax ${money(i.taxAmount)}${i.taxPercent ? ` (${i.taxPercent}%)` : ''}`,
          `Total ${money(i.total)}`
        ]);

        dueEditor.visible = editable;
        due.set(i.dueUtc ? plainDate(i.dueUtc) : null);

        issueButton.visible = i.isDraft;
        paidButton.visible = i.isIssued;
        voidButton.visible = editable;
        // A void invoice has nothing to send; the server would refuse it anyway.
        emailButton.visible = i.status !== InvoiceStatus.Void;

        renderLines(i, editable);
        lineEditor.visible = editable;
        if (!editable) resetLineEditor();

        summary.visible = linesSection.visible = historySection.visible = true;
        refreshButtons();
        relayout(content);
      }

      /** Lines grouped under their ticket heading, as the invoice and its PDF print them. */
      function renderLines(i: InvoiceDto, editable: boolean): void {
        linesHost.controls.clear();
        let heading: string | null = null;
        for (const line of i.lines) {
          if (line.ticketHeading !== heading) {
            heading = line.ticketHeading;
            const label = text(heading || 'Additional charges', { font: FONT.small, role: 'subtle' });
            label.margin = { left: 0, top: GAP.sm, right: 0, bottom: 0 };
            linesHost.controls.add(label);
          }
          linesHost.controls.add(buildLineCard(line, editable));
        }
        linesEmpty.update(i.lines.length);
        linesHost.layout();
      }

      function buildLineCard(line: InvoiceLineDto, editable: boolean): Card {
        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          value(line.description, { font: FONT.bold, wrap: true, autoWidth: false, hAlign: 'stretch' })
        );
        if (line.notes) left.controls.add(muted(line.notes));
        if (line.date) left.controls.add(muted(localDate(line.date)));

        const right = stackOf(
          vstack({ spacing: GAP.xs, autoWidth: true, align: 'end' }),
          value(money(line.amount), { font: FONT.bold }),
          value(`${line.quantity} × ${money(line.unitPrice)}`, { font: FONT.small, role: 'muted' })
        );
        if (editable) {
          right.controls.add(
            stackOf(
              hstack({ spacing: GAP.sm }),
              button('Edit', () => editLine(line)),
              button('Remove', () => void removeLine(line))
            )
          );
        }

        const row = splitRow({ align: 'start' });
        fillWith(row, left);
        row.controls.add(right);
        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function buildSentRow(e: InvoiceEmailDto): Card {
        const body = stackOf(
          vstack({ spacing: GAP.xs }),
          text(join([localDateTime(e.sentUtc), `to ${e.to}`, e.sentByName ? `by ${e.sentByName}` : null])),
          muted(e.subject)
        );
        if (e.error) body.controls.add(muted(e.error, 'danger'));
        if (e.bounceDetail) body.controls.add(muted(`Delivery failed: ${e.bounceDetail}`, 'danger'));
        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function buildPaymentRow(p: InvoicePaymentDto): Card {
        const label: Record<InvoicePaymentStatus, string> = {
          [InvoicePaymentStatus.Started]: 'Started - the customer went to PayPal',
          [InvoicePaymentStatus.Completed]: 'Paid',
          [InvoicePaymentStatus.Failed]: 'Failed',
          [InvoicePaymentStatus.Cancelled]: 'Cancelled',
          [InvoicePaymentStatus.Pending]: 'Held by PayPal - mark the invoice paid once it clears'
        };
        const role =
          p.status === InvoicePaymentStatus.Completed ? 'ok'
          : p.status === InvoicePaymentStatus.Failed ? 'danger'
          : p.status === InvoicePaymentStatus.Pending ? 'warn'
          : 'muted';
        const body = stackOf(
          vstack({ spacing: GAP.xs }),
          text(join([localDateTime(p.completedUtc ?? p.createdUtc), `${money(p.amount)} ${p.currency}`, p.sandbox ? 'sandbox' : null])),
          muted(label[p.status] ?? 'Unknown', role),
          muted(join([
            p.payerName || null,
            p.payerEmail || null,
            p.captureId ? `capture ${p.captureId}` : `order ${p.orderId}`
          ]))
        );
        if (p.error) body.controls.add(muted(p.error, p.status === InvoicePaymentStatus.Failed ? 'danger' : 'muted'));
        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function renderPayments(d: InvoicePaymentsDto): void {
        payState.text = d.available
          ? 'Customers can pay this invoice with PayPal from the link below. Invoice emails include it through {PayLine} or {PayLink}.'
          : (d.blocked ?? 'Online payment is not available.');
        payState.role = d.available ? 'muted' : 'subtle';
        payUrlBox.text = d.payUrl ?? '';
        payLinkRow.visible = (d.payUrl ?? '').length > 0;
        payWarning.text = d.warning ?? '';
        payWarning.visible = payWarning.text.length > 0;
        paymentList.set(d.payments);
        relayout(paySection);
        relayout(content);
      }

      async function loadPayments(): Promise<void> {
        try {
          const d = await crm.getInvoicePayments(invoiceId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          paySection.visible = true;
          renderPayments(d);
        } catch (error) {
          if (ctx.cancelled()) return;
          paySection.visible = true;
          payState.text = 'Could not read the online payments: ' + describeError(error);
          payState.role = 'danger';
          payLinkRow.visible = false;
          relayout(content);
        }
      }

      async function copyPayLink(): Promise<void> {
        const url = payUrlBox.text;
        if (!url) return;
        try {
          await navigator.clipboard.writeText(url);
          payStatus.ok('Pay link copied.');
        } catch {
          // Refused outside a secure context or without permission: the box is there to select from.
          payStatus.error('The browser would not copy it - select the link in the box and copy it from there.');
        }
      }

      function buildMailRow(m: InboundMailDto): Card {
        const body = stackOf(
          vstack({ spacing: GAP.xs }),
          text(join([localDateTime(m.receivedUtc), `from ${m.fromName ? `${m.fromName} <${m.fromAddress}>` : m.fromAddress}`])),
          text(m.subject, { font: FONT.bold })
        );
        if (m.body) body.controls.add(muted(m.body));
        if (m.note) body.controls.add(muted(m.note, 'subtle'));
        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function buildVersionCard(v: InvoiceVersionDto): Card {
        const heading = splitRow({ align: 'center' });
        fillWith(
          heading,
          text(join([`v${v.version}`, v.change, v.byUserName, localDateTime(v.atUtc)]), { font: FONT.small })
        );
        if (isEditable(invoice)) heading.controls.add(button('Restore', () => void restore(v)));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        for (const l of v.snapshot?.lines ?? []) {
          body.controls.add(
            muted(
              join([
                l.date ? localDate(l.date) : null,
                `${l.description} × ${l.quantity} @ ${money(l.unitPrice)} = ${money(l.amount)}`
              ])
            )
          );
        }
        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function renderMail(history: InvoiceEmailDto[], mail: InboundMailDto[]): void {
        sentList.set(history);
        sentSection.visible = history.length > 0;
        mailList.set(mail);
        mailSection.visible = mail.length > 0;
        sentLine.text = lastSentLine(history);
        sentLine.role = history.some(e => e.bounced) || (history[0] && !history[0].success) ? 'danger' : 'muted';
        relayout(content);
      }

      function refreshButtons(): void {
        for (const b of [saveDueButton, pdfButton, emailButton, issueButton, paidButton, voidButton, saveLineButton]) {
          b.enabled = !busy && invoice !== null;
        }
        sendButton.enabled = !sending && !blockedCard.visible;
      }

      function setBusy(value: boolean): void {
        busy = value;
        refreshButtons();
      }

      // =================================================================
      //  Loading
      // =================================================================

      async function loadInvoice(): Promise<void> {
        const [i, history] = await Promise.all([
          crm.getInvoice(invoiceId, ctx.dispose.signal()),
          crm.getInvoiceHistory(invoiceId, ctx.dispose.signal())
        ]);
        if (ctx.cancelled()) return;
        applyInvoice(i);
        historyList.set(history);
        relayout(content);
      }

      /**
       * The send log and the correspondence, loaded with the page: hiding them behind the Email
       * button would make an invoice that bounced look exactly like one that was never sent.
       */
      async function loadMail(): Promise<void> {
        try {
          const [history, mail] = await Promise.all([
            crm.getInvoiceEmailHistory(invoiceId, ctx.dispose.signal()),
            crm.getInvoiceCorrespondence(invoiceId, ctx.dispose.signal())
          ]);
          if (ctx.cancelled()) return;
          renderMail(history, mail);
        } catch (error) {
          if (!ctx.cancelled()) sentLine.text = 'Could not read the email history: ' + describeError(error);
        }
      }

      async function reloadAll(): Promise<void> {
        await Promise.all([loadInvoice(), loadMail(), loadPayments()]);
      }

      // =================================================================
      //  Actions
      // =================================================================

      async function run(
        working: string,
        call: () => Promise<unknown>,
        done: string,
        line: StatusLine = status
      ): Promise<boolean> {
        if (busy) return false;
        setBusy(true);
        // One outcome on screen at a time: a stale "Line removed." under the editor after a restore
        // reads as the restore having removed a line.
        for (const other of [status, lineStatus, historyStatus]) if (other !== line) other.clear();
        line.busy(working);
        try {
          await call();
          if (ctx.cancelled()) return false;
          line.ok(done);
          await reloadAll();
          return true;
        } catch (error) {
          if (!ctx.cancelled()) line.error(describeError(error));
          return false;
        } finally {
          if (!ctx.cancelled()) setBusy(false);
        }
      }

      async function saveDueDate(): Promise<void> {
        const iso = due.iso();
        await run('Saving the due date…', () => crm.updateInvoice(invoiceId, { dueUtc: calendarUtc(iso) }),
          iso ? `Due date set to ${iso}.` : 'Due date cleared.');
      }

      async function lifecycle(action: 'issue' | 'paid' | 'void'): Promise<void> {
        const i = invoice;
        if (!i) return;
        if (action === 'void') {
          const ok = await confirm(ctx.desktop, {
            title: 'Void invoice',
            message: `Void ${i.number}? Its time and purchases return to unbilled, and it can no longer be paid or edited.`
          });
          if (!ok || ctx.cancelled()) return;
          await run('Voiding…', () => crm.voidInvoice(invoiceId), `Voided ${i.number}.`);
        } else if (action === 'issue') {
          // A due date picked but not saved is used for the issue, as the XAML did.
          const picked = calendarUtc(due.iso());
          await run('Issuing…', () => crm.issueInvoice(invoiceId, picked), `Issued ${i.number}.`);
        } else {
          const ok = await confirm(ctx.desktop, {
            title: 'Mark paid',
            message: `Mark ${i.number} (${money(i.total)}) as paid today?`
          });
          if (!ok || ctx.cancelled()) return;
          await run('Marking paid…', () => crm.markInvoicePaid(invoiceId), `Marked ${i.number} paid.`);
        }
      }

      function openPdf(): void {
        const i = invoice;
        if (!i) return;
        // Called straight from the click, before any await: the new tab needs the click's activation.
        openInNewTab(crm.invoicePdfPath(invoiceId), `${i.number}.pdf`).catch(error => {
          if (!ctx.cancelled()) status.error('Could not open the PDF: ' + describeError(error));
        });
      }

      async function restore(v: InvoiceVersionDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Restore version',
          message: `Restore version ${v.version}? The current state is kept in history.`
        });
        if (!ok || ctx.cancelled()) return;
        await run(`Restoring version ${v.version}…`, () => crm.restoreInvoiceVersion(invoiceId, v.version),
          `Restored version ${v.version}.`, historyStatus);
      }

      // ---- lines ----

      function editLine(line: InvoiceLineDto): void {
        editingLineId = line.id;
        editorTitle.text = 'Edit line';
        saveLineButton.text = 'Save line';
        cancelLineButton.visible = true;
        lineDate.set(line.date ? localIsoOf(line.date) : null);
        // LiteDB reads a stored "" back as null, so a cleared field arrives as null here.
        lineDescription.text = line.description ?? '';
        lineQuantity.text = String(line.quantity);
        lineRate.set(line.unitPrice);
        lineNotes.text = line.notes ?? '';
        relayout(lineEditor);
        lineDescription.focus();
      }

      function resetLineEditor(): void {
        editingLineId = null;
        editorTitle.text = 'Add a line';
        saveLineButton.text = 'Add line';
        cancelLineButton.visible = false;
        lineDate.set(todayLocalIso());
        lineDescription.text = '';
        lineQuantity.text = '1';
        lineRate.set(null);
        lineNotes.text = '';
        relayout(lineEditor);
      }

      async function saveLine(): Promise<void> {
        const description = lineDescription.text.trim();
        if (!description) {
          lineStatus.error('Enter a description.');
          return;
        }
        const quantity = parseAmount(lineQuantity.text);
        if (quantity === null || quantity <= 0) {
          lineStatus.error('Enter a quantity greater than zero.');
          return;
        }
        const unitPrice = lineRate.value();
        if (unitPrice === null) {
          lineStatus.error('Enter a rate.');
          return;
        }
        const body = {
          description,
          notes: lineNotes.text.trim(),
          date: localNoonUtc(lineDate.iso()),
          quantity,
          unitPrice
        };
        const id = editingLineId;
        const ok = await run(
          id === null ? 'Adding the line…' : 'Saving the line…',
          () => (id === null ? crm.addInvoiceLine(invoiceId, body) : crm.updateInvoiceLine(invoiceId, id, body)),
          id === null ? 'Line added.' : 'Line updated.',
          lineStatus
        );
        if (ok) resetLineEditor();
      }

      async function removeLine(line: InvoiceLineDto): Promise<void> {
        const ok = await confirm(ctx.desktop, { title: 'Remove line', message: `Remove line "${line.description}"?` });
        if (!ok || ctx.cancelled()) return;
        const removed = await run(
          'Removing the line…',
          () => crm.removeInvoiceLine(invoiceId, line.id),
          'Line removed.',
          lineStatus
        );
        if (removed && editingLineId === line.id) resetLineEditor();
      }

      // ---- email ----

      async function openEmail(): Promise<void> {
        if (busy) return;
        setBusy(true);
        status.busy('Preparing the message…');
        try {
          const draft = await crm.getInvoiceEmailDraft(invoiceId, ctx.dispose.signal());
          if (ctx.cancelled()) return;

          recipients.controls.clear();
          for (const r of draft.suggestions) recipients.controls.add(recipientButton(r));
          toBox.text = draft.to;
          ccBox.text = '';
          subjectBox.text = draft.subject;
          bodyBox.text = draft.body;
          attachPdf.checked = true;
          // Sending a draft is nearly always a mistake, so this is offered pre-ticked - still a tick
          // the sender can see and clear, not a silent state change.
          markIssued.checked = invoice?.isDraft ?? false;
          markIssued.visible = invoice?.isDraft ?? false;

          blocked.text = draft.blocked ?? '';
          blockedCard.visible = !draft.canSend;
          for (const c of [toBox, ccBox, subjectBox]) c.enabled = draft.canSend;
          bodyBox.readOnly = !draft.canSend;
          attachPdf.enabled = draft.canSend;
          markIssued.enabled = draft.canSend;

          emailStatus.clear();
          compose.visible = true;
          status.clear();
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not prepare the message: ' + describeError(error));
        } finally {
          if (!ctx.cancelled()) setBusy(false);
        }
      }

      function recipientButton(r: InvoiceRecipientDto) {
        return button(`${r.name} · ${r.source}`, () => addRecipient(r));
      }

      /** Appends rather than replaces, and a second click on the same address does nothing. */
      function addRecipient(r: InvoiceRecipientDto): void {
        const current = toBox.text.trim();
        if (!current) {
          toBox.text = r.email;
          return;
        }
        if (current.toLowerCase().includes(r.email.toLowerCase())) return;
        toBox.text = current.replace(/[,\s]+$/, '') + ', ' + r.email;
      }

      function closeEmail(): void {
        compose.visible = false;
        relayout(content);
      }

      async function send(): Promise<void> {
        if (sending) return;
        if (!toBox.text.trim()) {
          emailStatus.error('Enter at least one recipient.');
          return;
        }
        sending = true;
        refreshButtons();
        emailStatus.busy('Sending…');
        try {
          const result = await crm.emailInvoice(invoiceId, {
            to: toBox.text,
            cc: ccBox.text,
            subject: subjectBox.text,
            body: bodyBox.text,
            attachPdf: attachPdf.checked,
            markIssued: markIssued.visible && markIssued.checked
          });
          if (ctx.cancelled()) return;
          if (result.success) {
            closeEmail();
            status.ok(result.message);
            // Sending can issue a draft, so the whole invoice is re-read, not just the mail log.
            await reloadAll();
          } else {
            // A refusal keeps the panel open with what was typed still in it.
            emailStatus.error(result.message);
            await loadMail();
          }
        } catch (error) {
          // A transport failure must not leave the panel pinned to "Sending…".
          if (!ctx.cancelled()) emailStatus.error('Could not reach the CRM: ' + describeError(error));
        } finally {
          sending = false;
          if (!ctx.cancelled()) refreshButtons();
        }
      }

      // =================================================================

      async function start(): Promise<void> {
        if (!Number.isInteger(invoiceId) || invoiceId <= 0) {
          title.text = 'Invoice not found';
          return;
        }
        setBusy(true);
        try {
          await loadInvoice();
        } catch (error) {
          if (ctx.cancelled()) return;
          title.text = 'Invoice not found';
          status.error(describeError(error));
          relayout(content);
          return;
        } finally {
          if (!ctx.cancelled()) setBusy(false);
        }
        await Promise.all([loadMail(), loadPayments()]);
      }

      void start();
    }
  };
}

